"use client";

import {
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { topbarHeight } from "@/utils/topbar";

/** Only mounted on an opted-in page. Existing header markup and scroll behaviour are retained. */
export default function TopbarLayout({
  bar,
  children,
  height,
  devices,
  background,
}: {
  bar: ReactNode;
  children: ReactNode;
  height?: string | number;
  devices?: "m" | "d";
  background?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const sizes = topbarHeight(height);

  useLayoutEffect(() => {
    const container = root.current;
    const element = strip.current;
    if (!container || !element) return;
    let frame = 0;
    let disposed = false;
    const measure = () => {
      frame = 0;
      if (disposed) return;
      const offset = Math.max(0, element.getBoundingClientRect().bottom);
      const next = `${offset}px`;
      if (container.style.getPropertyValue("--re-topbar-offset") !== next) {
        container.style.setProperty("--re-topbar-offset", next);
        window.dispatchEvent(new Event("reweb:header-position"));
      }
    };
    const schedule = () => {
      if (!disposed && !frame) frame = requestAnimationFrame(measure);
    };
    measure();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(schedule)
        : undefined;
    observer?.observe(element);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.fonts?.ready.then(schedule);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [height, devices, bar]);

  return (
    <div
      ref={root}
      data-re-topbar-layout
      data-topbar-devices={devices}
      className="re-topbar-layout"
      style={
        {
          "--re-topbar-height": sizes.mobile,
          "--re-topbar-height-desktop": sizes.desktop,
          "--re-topbar-background": background,
        } as CSSProperties
      }
    >
      <div
        ref={strip}
        data-re-topbar
        role="region"
        aria-label="Information fra hjemmesiden"
        className="re-topbar"
      >
        {bar}
      </div>
      {children}
    </div>
  );
}
